import React from "react";

import { ActionType, TriggerType } from "../../../../enums";
import { ActivityTransition, IAction, ITrigger } from "../../../../types";
import { Workflow } from "../../../../workflowModel";
import {
    AttributeDataChangePage,
    CallMethodPage,
    CallScriptPage,
    IncrementValuePage,
    MenuPage,
    NewObjectPage,
    OpenFormPage,
    SendMailPage,
    SendSmsPage,
    SendWfMessagePage,
    SetAttributePage,
    SetObjectAttributePage,
    SetValuePage,
    WfMessagePage,
} from "./pages";
import { ActionPageProps, TriggerPageProps } from "./pages/types";

export const actionPageRenderer = (hostKey: string, { actionType, properties }: IAction, updateAction: ActionPageProps["updateAction"]) => {
    switch (actionType) {
        case ActionType.CallMethod:
            return <CallMethodPage {...{ hostKey, properties, updateAction }} />;
        case ActionType.CallScript:
            return <CallScriptPage {...{ hostKey, properties, updateAction }} />;
        case ActionType.IncrementValue:
            return <IncrementValuePage {...{ hostKey, properties, updateAction }} />;
        case ActionType.SendMail:
            return <SendMailPage {...{ hostKey, properties, updateAction }} />;
        case ActionType.SendSMS:
            return <SendSmsPage {...{ hostKey, properties, updateAction }} />;
        case ActionType.SendWFMessage:
            return <SendWfMessagePage {...{ hostKey, properties, updateAction }} />;
        case ActionType.SetAttribute:
            return <SetAttributePage {...{ hostKey, properties, updateAction }} />;
        case ActionType.SetObjectAttribute:
            return <SetObjectAttributePage {...{ hostKey, properties, updateAction }} />;
        case ActionType.SetValue:
            return <SetValuePage {...{ hostKey, properties, updateAction }} />;
        case ActionType.OpenForm:
            return <OpenFormPage {...{ hostKey, properties, updateAction }} />;
        default:
            throw new Error(`Action page of type "${actionType}" is not implemented!`);
    }
};

export const triggerPageRenderer = (hostKey: string, { triggerType, properties }: ITrigger, updateTrigger: TriggerPageProps["updateTrigger"]) => {
    switch (triggerType) {
        case TriggerType.AttributeDataChange:
            return <AttributeDataChangePage {...{ hostKey, properties, updateTrigger }} />;
        case TriggerType.Menu:
            return <MenuPage {...{ hostKey, properties, updateTrigger }} />;
        case TriggerType.NewObject:
            return <NewObjectPage />;
        case TriggerType.WfMessage:
            return <WfMessagePage {...{ hostKey, properties, updateTrigger }} />;
        default:
            throw new Error(`Trigger page of type "${triggerType}" is not implemented!`);
    }
};

/**
 * Vrací seznam všech propojení vedoucích do specifikované aktivity v rámci všech sad.
 * @param activityId Id aktivity, pro kterou se mají hledat všechna propojení vedoucí do ní.
 */
export const getTransitionsToActivity = (activityId: string, workflow: Workflow): readonly ActivityTransition[] => {
    const toActivityTransitions: ActivityTransition[] = [];

    for (const activity of workflow.activities) {
        for (const transition of activity.transitions) {
            if (transition.to === activityId) {
                toActivityTransitions.push({
                    transitionName: transition.name,
                    transitionCondition: transition.condition,
                    otherActivityName: activity.name,
                });
            }
        }
    }

    return toActivityTransitions;
};

/**
 * Vrací seznam všech propojení vedoucích ze specifikované aktivity v rámci všech sad.
 * @param activityId Id aktivity, pro kterou se mají hledat všechna propojení vedoucí z ní.
 */
export const getTransitionsFromActivity = (activityId: string, workflow: Workflow): readonly ActivityTransition[] => {
    const activity = workflow.activities.find(act => act.id === activityId);

    if (activity == null)
        throw new Error(`Activity of id '${activityId}' does not exist! Could not get transitions from activity.`);

    return activity.transitions.map(transition => ({
        transitionName: transition.name,
        otherActivityName: workflow.activities.find(act => act.id === transition.to)?.name ?? "",
        transitionCondition: transition.condition,
    }));
};
